import React, { useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet } from 'lucide-react';

const Reports = () => {
  const [generating, setGenerating] = useState({});

  const exportToCSV = async (reportType, endpoint, filename) => {
    setGenerating({ ...generating, [reportType]: true });
    try {
      const response = await axiosInstance.get(endpoint);
      const data = response.data;
      
      if (!data || data.length === 0) {
        toast.error('No data available for export');
        return;
      }

      // Convert to CSV
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(header => {
          const val = row[header];
          if (val === null || val === undefined) return '';
          if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
          return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
        }).join(','))
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setGenerating({ ...generating, [reportType]: false });
    }
  };

  const reports = [
    {
      id: 'clients',
      title: 'Clients Report',
      description: 'Export all client information including AUM, status, and payout history',
      endpoint: '/clients',
      filename: 'clients_report.csv',
      icon: 'users'
    },
    {
      id: 'ledger',
      title: 'Capital Ledger Report',
      description: 'Export all ledger entries with client details and approval status',
      endpoint: '/ledger',
      filename: 'ledger_report.csv',
      icon: 'book'
    },
    {
      id: 'cycles',
      title: 'Cycles Report',
      description: 'Export all profit cycles with rates and status',
      endpoint: '/cycles',
      filename: 'cycles_report.csv',
      icon: 'refresh'
    },
    {
      id: 'expenses',
      title: 'Expenses Report',
      description: 'Export all expense records with approval status',
      endpoint: '/expenses',
      filename: 'expenses_report.csv',
      icon: 'receipt'
    },
    {
      id: 'conversions',
      title: 'Cash Conversions Report',
      description: 'Export all cash to USDT conversion records',
      endpoint: '/cash-pool/conversions',
      filename: 'cash_conversions_report.csv',
      icon: 'convert'
    }
  ];

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="cb-page-header">
        <p className="cb-eyebrow">Statements</p>
        <h1 className="cb-h1">Reports</h1>
        <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)' }}>Export data from different modules as CSV files</p>
        <hr className="cb-divider" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => (
          <div key={report.id} className="cb-card" data-testid={`report-${report.id}`}>
            <div className="flex items-start justify-between mb-4">
              <FileSpreadsheet className="w-7 h-7 cb-card-icon" />
            </div>
            <h3 className="cb-section-title">{report.title}</h3>
            <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)', marginBottom: 'var(--cb-space-4)' }}>{report.description}</p>
            <button
              onClick={() => exportToCSV(report.id, report.endpoint, report.filename)}
              data-testid={`export-${report.id}-button`}
              className="cb-btn-primary w-full"
              style={{ justifyContent: 'center' }}
              disabled={generating[report.id]}
            >
              <FileDown className="w-4 h-4" />
              {generating[report.id] ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="cb-notice items-start" style={{ marginTop: 'var(--cb-space-7)', cursor: 'default' }}>
        <div className="flex-1">
          <p className="cb-notice-title">Export Information</p>
          <ul className="cb-notice-sub" style={{ listStyle: 'none', padding: 0, marginTop: 'var(--cb-space-2)' }}>
            <li>• All reports are exported in CSV format compatible with Excel</li>
            <li>• Data includes all records from the system (approved and pending)</li>
            <li>• For cycle-specific payouts, view payouts from the Cycles page</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reports;
